import { useEffect, useState, useCallback, useRef } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import {
  ButtonPseudo,
  SidePanel,
  SidePanelType,
  Popup,
  PermissionEnum,
  ButtonSecondary,
  ResponseError,
  ButtonPrimary,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  deleteIsSuccess,
  clearDocumentState,
  onDocumentsMoveById,
  onDocumentRaname,
  setNeedToReloadCategoryId,
} from '../store/slices/documents/documents';
import { CustomerDocumentStatus, CustomerStatus, CustomerType } from '../types';
import {
  useLazyGetCustomerDocumentFileQuery,
  useLazyGetCustomerDocumentQuery,
  useDeleteCustomerDocumentMutation,
  useChangeCustomerDocumentsStatusMutation,
  useChangeCustomerDocumentsCategoryMutation,
  useChangeDocumentExpirationDateMutation,
  useLazyGetTransactionInDocumentQuery,
  useRenameCustomerDocumentMutation,
  useLazyGetCustomerDocumentsQuery,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
} from '../store/services/customers';
import { useAddCustomersCommentMutation } from '../../../store/documents/services';
import DocumentInfo from '../../../components/partial/documents/info';
import { DocumentInfoTypes } from './types';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const DocumentInfoHOC = ({
  customerType,
  withoutControls,
}: DocumentInfoTypes) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [imgLink, setImgLink] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<
    CustomerDocumentStatus | undefined
  >(undefined);
  const [changeDateError, setChangeDateError] = useState('');
  const [documentTransactionType, setDocumentTransactionType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    customerDocuments: { info, allowedCategories },
    customersDictionaries: { activePermissions, customersTransactionTypes },
    // individualCustomers: {
    //   info: {
    //     general: { baseInformation: individualBaseInformation },
    //   },
    // },
    // corporateCustomers: {
    //   info: {
    //     general: { baseInformation: corporateBaseInformation },
    //   },
    // },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  const { documentId, customerId } = useParams();

  const { hash, search } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [getDocument, { isLoading }] = useLazyGetCustomerDocumentQuery();
  const [getDocumentFile, { isLoading: fileIsLoading, isFetching }] =
    useLazyGetCustomerDocumentFileQuery();
  const [deleteDocument, { isLoading: deleteInProgress }] =
    useDeleteCustomerDocumentMutation();
  const [
    changeCustomerDocumentsStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeCustomerDocumentsStatusMutation();
  const [changeCustomerDocumentsCategory, { isLoading: moveInProgress }] =
    useChangeCustomerDocumentsCategoryMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCustomersCommentMutation();
  const [changeDocumentExpDate, { isLoading: updateInProgress }] =
    useChangeDocumentExpirationDateMutation();
  const [getTransaction] = useLazyGetTransactionInDocumentQuery();
  const [rename, { isLoading: documentRenameInProgress }] =
    useRenameCustomerDocumentMutation();
  const [getCustomerDocuments] = useLazyGetCustomerDocumentsQuery();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const addCommentAction = (text: string) => {
    addComment({
      id: documentId as string,
      body: {
        text,
      },
    });
  };

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: customerId as string,
        documentId: info?.id as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response && !response.error) {
        dispatch(
          onDocumentRaname({
            name: documentName,
            type: info?.documentCategoryId as string,
            documentId: info?.id as string,
          }),
        );
      } else if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const getTransactionAction = useCallback(async (transactionId: string) => {
    const response = (await getTransaction(transactionId)) as {
      data: { data: { typeId: string } };
    };

    if (response && response.data && response.data.data) {
      const currentType = customersTransactionTypes?.find(
        (item) => item.id === response.data.data.typeId,
      );

      if (currentType) {
        setDocumentTransactionType(currentType.name);
      }
    }
  }, []);

  // let userName;
  let link: string;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
      customerId: customerId as string,
    });

    // userName = `${individualBaseInformation?.firstName} ${individualBaseInformation?.lastName}`;
  } else if (customerType === CustomerType.CORPORATE) {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
      customerId: customerId as string,
    });

    // userName = corporateBaseInformation?.name;
  }

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDocumentName('');
    setNameError('');

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  }, []);

  const onDownload = () => {
    if (imgLink) {
      const hiddenElement = document.createElement('a');
      // @ts-ignore
      hiddenElement.href = imgLink;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${info?.name}`;
      hiddenElement.click();
    }
  };

  const onChangeDocumentExpDate = async (date: string) => {
    const response = (await changeDocumentExpDate({
      id: documentId as string,
      body: {
        id: documentId as string,
        expirationDate: date,
      },
    })) as { error?: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setChangeDateError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setChangeDateError(response?.error?.data?.message);
      } else {
        setChangeDateError('Internal Server Error. Try Again');
      }
    }
  };

  const onDelete = async () => {
    const response = await deleteDocument({
      id: customerId as string,
      documentId: info?.id as string,
    });

    if (response) {
      getStats(customerId as string);

      dispatch(deleteIsSuccess({ ids: [info?.id as string], id: info?.id }));

      setDeletePopupVisible(false);

      handleClose();
    }
  };

  const changeCustomerDocumentsCategoryAction = async (
    documentCategoryId: string,
  ) => {
    const body: {
      ids: string[];
      documentCategoryId?: string | undefined;
    } = {
      ids: [documentId as string],
      documentCategoryId,
    };

    const response = (await changeCustomerDocumentsCategory({
      id: customerId as string,
      body,
    })) as { error?: any };

    if (response && !response.error) {
      getStats(customerId as string);

      let sort = 'asc';
      let sortProp = 'createdAt';

      const movedDocument: {
        documentCategoryId: string;
        ids: string[];
        type?: string;
        id: string;
      } = {
        documentCategoryId,
        ids: [documentId as string],
        id: documentId as string,
      };

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed.sort) {
          sort = parsed.sort as string;
        }

        if (parsed.sortProp) {
          sortProp = parsed.sortProp as string;
        }

        if (parsed.documentCategoryId) {
          movedDocument.type = parsed.documentCategoryId as string;
        } else {
          movedDocument.type = 'all';
        }
      } else {
        movedDocument.type = 'all';
      }

      dispatch(onDocumentsMoveById(movedDocument));

      if (
        movedDocument.type &&
        movedDocument.type === movedDocument.documentCategoryId
      ) {
        if (movedDocument.type !== 'all') {
          getCustomerDocuments({
            id: customerId as string,
            categoryId: movedDocument.documentCategoryId,
            take: '20',
            reload: true,
            sort,
            sortProp,
          });
        }
      } else {
        dispatch(setNeedToReloadCategoryId(movedDocument.documentCategoryId));
      }
    }
  };

  const getDocumentFileAction = async () => {
    const response = await getDocumentFile({
      id: customerId as string,
      documentId: documentId as string,
    });

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);

      setImgLink(blob);
    }
  };

  useEffect(() => {
    if (info && !documentName) {
      setDocumentName(info.name);
    }
  }, [info, documentName]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedStatus(undefined);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!info && documentId && customerId) {
      getDocument({ id: customerId, documentId });
      getDocumentFileAction();
    }
  }, [documentId, info, customerId]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearDocumentState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        textareaRef?.current?.focus();
      }, 100);
    }
  }, [hash, textareaRef]);

  useEffect(() => {
    if (info && info?.transactionId) {
      getTransactionAction(info?.transactionId);
    }
  }, [info]);

  const organizationStatus =
    customerType === CustomerType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  return (
    <>
      <Popup
        title={`Delete ${documentName}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={documentName || ''}
        sidepanelExpandedKey="customer-document"
        footer={
          <>
            {((customerType === CustomerType.INDIVIDUAL &&
              activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
              )) ||
              (customerType === CustomerType.CORPORATE &&
                activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                ))) &&
              info &&
              organizationStatus !== CustomerStatus.CLOSED && (
                <>
                  <ButtonPrimary
                    text={documentRenameInProgress ? 'Loading...' : 'Save'}
                    disabled={
                      info.name === documentName || documentRenameInProgress
                    }
                    onClick={onRename}
                    bold
                  />
                  {info.status !== CustomerDocumentStatus.ACCEPTED &&
                    info.status !== CustomerDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === CustomerDocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(CustomerDocumentStatus.ACCEPTED);

                          changeCustomerDocumentsStatus({
                            id: customerId as string,
                            body: {
                              ids: [info?.id],
                              status: CustomerDocumentStatus.ACCEPTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                  {info.status !== CustomerDocumentStatus.REJECTED &&
                    info.status !== CustomerDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === CustomerDocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={cn.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(CustomerDocumentStatus.REJECTED);

                          changeCustomerDocumentsStatus({
                            id: customerId as string,
                            body: {
                              ids: [info?.id],
                              status: CustomerDocumentStatus.REJECTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                </>
              )}
            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className="button-white"
              bold
            />
          </>
        }
      >
        <DocumentInfo
          isLoading={isLoading}
          info={info}
          setDeletePopupVisible={setDeletePopupVisible}
          moveInProgress={moveInProgress}
          onMove={(data) => changeCustomerDocumentsCategoryAction(data)}
          withoutControls={withoutControls}
          imgLink={imgLink}
          fileIsLoading={fileIsLoading}
          downloadInProgress={isFetching}
          commentsVisible={activePermissions?.includes(
            PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
          )}
          updateAllowed={activePermissions?.includes(
            PermissionEnum.DOCUMENTS_UPDATE_ACTION,
          )}
          documentName={documentName}
          setDocumentName={setDocumentName}
          commentInProgress={commentInProgress}
          sendComment={(text) => addCommentAction(text)}
          customerType={customerType}
          deleteIsVisible={
            (customerType === CustomerType.INDIVIDUAL &&
              activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
              )) ||
            (customerType === CustomerType.CORPORATE &&
              activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
              ))
          }
          onDownload={onDownload}
          textareaRef={textareaRef}
          onChangeDocumentExpDate={onChangeDocumentExpDate}
          changeDateError={changeDateError}
          updateInProgress={updateInProgress}
          documentTransactionType={documentTransactionType}
          allowedCategories={allowedCategories}
          nameError={nameError || (errors?.name?.message as string)}
          renameAvailable={
            customerType === CustomerType.INDIVIDUAL
              ? activePermissions?.includes(
                  PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
                )
              : activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
                )
          }
          status={organizationStatus}
          // sendMessage={(text) => dispatch(sendMessage(text))}
          // userName={userName}
        />
      </SidePanel>
    </>
  );
};

export default DocumentInfoHOC;
